import { useCallback, useMemo } from 'react';
import type { DxReportPatientInfo } from '../types';

interface PatientInfoSectionProps {
    value: DxReportPatientInfo;
    onChange: (next: DxReportPatientInfo) => void;
    caseType?: string | null;
    subCaseType?: string | null;
}

function computeAge(dobIso: string | null): number | null {
    if (!dobIso) return null;
    const dob = new Date(dobIso);
    if (Number.isNaN(dob.getTime())) return null;
    const now = new Date();
    let years = now.getFullYear() - dob.getFullYear();
    const m = now.getMonth() - dob.getMonth();
    if (m < 0 || (m === 0 && now.getDate() < dob.getDate())) years -= 1;
    return years >= 0 ? years : null;
}

export function PatientInfoSection({
    value,
    onChange,
    caseType,
    subCaseType
}: PatientInfoSectionProps) {
    const handleChange = useCallback(
        (patch: Partial<DxReportPatientInfo>) => {
            onChange({ ...value, ...patch });
        },
        [value, onChange]
    );

    const handleDobChange = useCallback(
        (dob: string) => {
            const isoOrNull = dob.trim() === '' ? null : dob;
            onChange({
                ...value,
                dateOfBirth: isoOrNull,
                age: computeAge(isoOrNull)
            });
        },
        [value, onChange]
    );

    const ageDisplay = useMemo(() => {
        if (typeof value.age === 'number') return `${value.age} y.o.`;
        return '—';
    }, [value.age]);

    return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                    Patient name
                </label>
                <input
                    type="text"
                    value={value.patientName}
                    onChange={(e) => handleChange({ patientName: e.target.value })}
                    maxLength={200}
                    className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                />
            </div>

            <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                    Date of birth
                </label>
                <input
                    type="date"
                    value={value.dateOfBirth ?? ''}
                    onChange={(e) => handleDobChange(e.target.value)}
                    className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                />
            </div>

            <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Age</label>
                <input
                    type="text"
                    value={ageDisplay}
                    readOnly
                    className="appearance-none block w-full px-3 py-2 border border-gray-200 rounded-md text-sm text-gray-500 bg-gray-50 cursor-not-allowed"
                />
            </div>

            <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                    Treating doctor
                </label>
                <input
                    type="text"
                    value={value.doctorName}
                    onChange={(e) => handleChange({ doctorName: e.target.value })}
                    maxLength={200}
                    className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                />
            </div>

            <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Practice</label>
                <input
                    type="text"
                    value={value.practice ?? ''}
                    onChange={(e) => handleChange({ practice: e.target.value })}
                    maxLength={200}
                    placeholder="e.g. Hamilton Dental"
                    className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                />
            </div>

            <div className="md:col-span-2">
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                    Report date
                </label>
                <input
                    type="date"
                    value={value.reportDate}
                    onChange={(e) => handleChange({ reportDate: e.target.value })}
                    className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                />
            </div>

            <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Case type</label>
                <input
                    type="text"
                    value={caseType ?? '—'}
                    readOnly
                    className="appearance-none block w-full px-3 py-2 border border-gray-200 rounded-md text-sm text-gray-500 bg-gray-50 cursor-not-allowed"
                />
            </div>

            <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                    Sub case type
                </label>
                <input
                    type="text"
                    value={subCaseType ?? '—'}
                    readOnly
                    className="appearance-none block w-full px-3 py-2 border border-gray-200 rounded-md text-sm text-gray-500 bg-gray-50 cursor-not-allowed"
                />
            </div>
        </div>
    );
}
